import type { MonitorConfig } from "@/types/monitor.types";
import type { QuoteTick } from "@/types/domain/market-data";
import type { Position } from "@/types/position.types";
import type { NumericLike, TastytradeBalance } from "@/types/tastytrade";

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type JsonRecord = Record<string, JsonValue>;

export type BalanceValue =
  | NumericLike
  | { value?: NumericLike | null | undefined }
  | null
  | undefined;

export type BalanceRecord =
  Partial<Record<keyof TastytradeBalance, BalanceValue>> &
  Record<string, BalanceValue>;

export type NormalizedBalance = Partial<Record<keyof TastytradeBalance, number | string>> &
  Record<string, number | string>;

export interface BalanceStreamPayload {
  data?: BalanceStreamPayload | null;
  "account-number"?: string;
  accountNumber?: string;
  account?: {
    "account-number"?: string;
    accountNumber?: string;
  } | null;
  balance?: BalanceRecord | null;
  balances?: BalanceRecord | null;
  "account-balance"?: BalanceRecord | null;
}

export type PositionsApiResponse =
  | Position[]
  | {
      data?: Position[] | { positions?: Position[]; items?: Position[] } | null;
      positions?: Position[];
      items?: Position[];
    }
  | {
      positions?: Position[];
      items?: Position[];
    };

export type MonitorTemplateConfig = MonitorConfig | JsonRecord;

export interface DashboardQuoteUpdate extends QuoteTick {
  event?: string;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isPositionCandidate(value: unknown): value is Position {
  return (
    isPlainObject(value) &&
    typeof value.symbol === "string" &&
    "quantity" in value
  );
}

function collectPositions(source: unknown, bucket: Position[]): void {
  if (!Array.isArray(source)) return;
  source.forEach((entry) => {
    if (isPositionCandidate(entry)) {
      bucket.push(entry);
    }
  });
}

export function extractPositionsFromResponse(response: unknown): Position[] {
  const positions: Position[] = [];

  if (Array.isArray(response)) {
    collectPositions(response, positions);
    return positions;
  }

  if (!isPlainObject(response)) {
    return positions;
  }

  collectPositions(response.positions, positions);
  collectPositions(response.items, positions);

  const data = response.data;
  if (Array.isArray(data)) {
    collectPositions(data, positions);
  } else if (isPlainObject(data)) {
    collectPositions(data.positions, positions);
    collectPositions(data.items, positions);
  }

  return positions;
}
